/**
 * Deep links of the app: the account links of the game's e-mails (https://<site>/link/?a=verify#t=<token> and
 * ?a=reset), which Android hands to the app through App Links (the manifest's filter for the site's /link/ path,
 * verified by https://<site>/.well-known/assetlinks.json: src/server/node/site-files.ts) or the "Open in the app"
 * intent of the link page (the token in the query there). Capacitor's App plugin delivers the URL of the launch
 * (getLaunchUrl) and of every later one (appUrlOpen). Without the native shell nothing happens: a browser opens the
 * link page (public/link/), which does the same through the API.
 */
import { nativePlugin } from './capacitor';

export interface AccountLink {
  action: 'verify' | 'reset';
  token: string;
}

/** The account link of a URL (the same rules as public/link/link.js parseLink), or null. */
export function parseAccountLink(url: string): AccountLink | null {
  let u: URL;
  try {
    u = new URL(url);
  } catch {
    return null;
  }
  if (u.protocol !== 'https:' || !/^\/link\/?$/.test(u.pathname)) return null;
  const action = u.searchParams.get('a');
  const token = new URLSearchParams(u.hash.replace(/^#/, '')).get('t') ?? u.searchParams.get('t');
  if ((action !== 'verify' && action !== 'reset') || !token || !/^[A-Za-z0-9_-]{16,256}$/.test(token)) return null;
  return { action, token };
}

/** Calls `onLink` with the account link the app was opened with and with every later one. Returns false on the web. */
export function installDeepLinks(onLink: (link: AccountLink) => void, win: any = globalThis): boolean {
  const app = nativePlugin('App', win);
  if (!app) return false;
  const seen = new Set<string>();
  const handle = (url: unknown) => {
    const link = typeof url === 'string' ? parseAccountLink(url) : null;
    // (the launch URL and the first appUrlOpen may be the same link)
    if (!link || seen.has(link.token)) return;
    seen.add(link.token);
    onLink(link);
  };
  app.listen('appUrlOpen', (e) => handle(e?.url));
  void app.call('getLaunchUrl').then(
    (r) => handle(r?.url),
    () => undefined,
  );
  return true;
}
